import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Loader2, CheckCircle2, Circle, ShieldCheck, AlertCircle, ChevronRight, UserCircle,
  ScrollText, Camera, FileCheck, Briefcase, CreditCard, Clock, Lock,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  getVerificationState, acknowledgeRightToWork, savePersonalInfo,
} from "@/lib/helper-verification.functions";
import { submitKycForReview } from "@/lib/helper-kyc.functions";

export const Route = createFileRoute("/_authenticated/helper/verification")({
  head: () => ({ meta: [{ title: "Professional verification — Peopels" }, { name: "robots", content: "noindex" }] }),
  component: VerificationPage,
});

type State = Awaited<ReturnType<typeof getVerificationState>>;

const STATUS_META: Record<string, { label: string; tone: string; body: string }> = {
  draft: { label: "Draft", tone: "bg-muted text-muted-foreground", body: "Complete every step then submit for review." },
  submitted: { label: "Submitted", tone: "bg-amber-100 text-amber-900", body: "Awaiting human review — typically within 24 hours." },
  pending: { label: "Pending verification", tone: "bg-amber-100 text-amber-900", body: "A reviewer is checking your documents. We'll email you the result." },
  more_info_required: { label: "More information required", tone: "bg-amber-100 text-amber-900", body: "A reviewer needs more from you — see notes below and re-upload, then resubmit." },
  verified: { label: "Verified", tone: "bg-emerald-100 text-emerald-900", body: "Your profile is live, visible in search, and can accept jobs." },
  rejected: { label: "Rejected", tone: "bg-destructive/10 text-destructive", body: "Verification was rejected. Contact support if you believe this is in error." },
  suspended: { label: "Suspended", tone: "bg-destructive/10 text-destructive", body: "Your account has been suspended. Contact support." },
};

function VerificationPage() {
  const navigate = useNavigate();
  const fetchState = useServerFn(getVerificationState);
  const ackRtw = useServerFn(acknowledgeRightToWork);
  const savePI = useServerFn(savePersonalInfo);
  const submit = useServerFn(submitKycForReview);

  const [loading, setLoading] = useState(true);
  const [state, setState] = useState<State | null>(null);
  const [busy, setBusy] = useState(false);

  // Personal info form
  const [pi, setPi] = useState({ fullName: "", dateOfBirth: "", addressLine1: "", addressLine2: "", city: "London", postcode: "", phone: "" });
  const [rtwChecked, setRtwChecked] = useState(false);

  async function refresh() {
    const s = await fetchState();
    setState(s as State);
    if (s.profile) {
      setPi({
        fullName: s.profile.full_name ?? "",
        dateOfBirth: s.profile.date_of_birth ?? "",
        addressLine1: s.profile.address_line1 ?? "",
        addressLine2: "",
        city: s.profile.city ?? "London",
        postcode: s.profile.postcode ?? "",
        phone: s.profile.phone ?? "",
      });
    }
    setRtwChecked(Boolean(s.helper?.right_to_work_acknowledged_at));
  }

  useEffect(() => {
    supabase.auth.getUser().then(async ({ data }) => {
      if (!data.user) { navigate({ to: "/auth", search: { redirect: "/helper/verification" } }); return; }
      try { await refresh(); } finally { setLoading(false); }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onSavePersonal() {
    setBusy(true);
    try {
      await savePI({ data: pi });
      toast.success("Personal details saved");
      await refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not save");
    } finally { setBusy(false); }
  }

  async function onAckRtw() {
    if (!rtwChecked) { toast.error("Please tick the box to confirm"); return; }
    setBusy(true);
    try {
      await ackRtw({ data: { confirmed: true } });
      toast.success("Right to Work confirmed");
      await refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not save");
    } finally { setBusy(false); }
  }

  async function onSubmitForReview() {
    setBusy(true);
    try {
      await submit();
      toast.success("Submitted — we'll email you within 24 hours.");
      await refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not submit");
    } finally { setBusy(false); }
  }

  if (loading || !state) {
    return (
      <div className="flex min-h-screen flex-col bg-surface">
        <Header />
        <main className="grid flex-1 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></main>
        <Footer />
      </div>
    );
  }

  const s = state.steps;
  const allBeforeReview =
    s.account && s.personalInfo && s.rightToWork && s.identityDoc && s.selfie && s.addressProof && s.professionalInfo && s.stripe;
  const statusMeta = STATUS_META[state.status] ?? STATUS_META.draft;
  const canSubmit = allBeforeReview && (state.status === "draft" || state.status === "more_info_required" || state.status === "rejected");

  const stepList = [
    { key: "account", icon: UserCircle, title: "Basic account", body: "Your name, email, mobile and password." , done: s.account, action: <Button asChild size="sm" variant="outline"><Link to="/dashboard">Edit account</Link></Button> },
    { key: "personalInfo", icon: ScrollText, title: "Personal information", body: "Full legal name, date of birth, UK address.", done: s.personalInfo, action: null, expand: "personal" as const },
    { key: "rightToWork", icon: ShieldCheck, title: "Right to Work declaration", body: "Required by UK law for paid work.", done: s.rightToWork, action: null, expand: "rtw" as const },
    { key: "identityDoc", icon: FileCheck, title: "Identity document", body: "Passport, UK driving licence, or BRP.", done: s.identityDoc, action: <Button asChild size="sm" variant="outline"><Link to="/helper/kyc">Upload</Link></Button> },
    { key: "selfie", icon: Camera, title: "Selfie verification", body: "Selfie holding your ID for face match.", done: s.selfie, action: <Button asChild size="sm" variant="outline"><Link to="/helper/kyc">Upload</Link></Button> },
    { key: "addressProof", icon: ScrollText, title: "Proof of address", body: "Utility bill, council tax, or bank statement (last 3 months).", done: s.addressProof, action: <Button asChild size="sm" variant="outline"><Link to="/helper/kyc">Upload</Link></Button> },
    { key: "professionalInfo", icon: Briefcase, title: "Professional information", body: "Services, experience, bio, profile photo, hourly rate.", done: s.professionalInfo, action: <Button asChild size="sm" variant="outline"><Link to="/helper/onboard/wizard">Set up</Link></Button> },
    { key: "stripe", icon: CreditCard, title: "Stripe Connect payouts", body: "Identity-verified payout account.", done: s.stripe, action: <Button asChild size="sm" variant="outline"><Link to="/become-helper">Connect</Link></Button> },
    { key: "review", icon: Lock, title: "Manual review by Peopels", body: "We review every professional within 24 hours.", done: s.review, action: null, expand: "submit" as const },
  ];

  const doneCount = stepList.filter((x) => x.done).length;
  const percent = Math.round((doneCount / stepList.length) * 100);




  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <Header />
      <main id="main-content" className="mx-auto w-full max-w-3xl flex-1 px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-primary">
          <ShieldCheck className="h-3.5 w-3.5" /> Professional verification
        </div>
        <h1 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">Become a verified Professional</h1>
        <p className="mt-2 text-sm text-muted-foreground sm:text-base">
          Peopels is trust-first. You cannot accept jobs, appear in search, or receive payouts until every step below is complete and a reviewer has approved your account.
        </p>

        {/* Status banner */}
        <div className={`mt-6 rounded-2xl p-4 ${statusMeta.tone}`}>
          <div className="flex items-start gap-3">
            {state.status === "verified" ? <CheckCircle2 className="mt-0.5 h-5 w-5" /> : <AlertCircle className="mt-0.5 h-5 w-5" />}
            <div className="min-w-0">
              <p className="font-display text-base font-bold">Status: {statusMeta.label}</p>
              <p className="mt-0.5 text-sm">{statusMeta.body}</p>
              {state.moreInfoNotes && state.status === "more_info_required" && (
                <p className="mt-2 rounded-md bg-white/60 p-2 text-xs"><strong>Reviewer notes:</strong> {state.moreInfoNotes}</p>
              )}
              {state.helper?.kyc_reviewer_notes && state.status === "rejected" && (
                <p className="mt-2 rounded-md bg-white/60 p-2 text-xs"><strong>Reviewer notes:</strong> {state.helper.kyc_reviewer_notes}</p>
              )}
            </div>
          </div>
        </div>

        {/* Progress */}
        <div className="mt-6 rounded-2xl border border-border bg-card p-5 shadow-card">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-medium">{doneCount} of {stepList.length} steps complete</p>
            <span className="font-display text-2xl font-extrabold tabular-nums">{percent}%</span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full bg-gradient-to-r from-primary to-emerald-500 transition-all duration-500" style={{ width: `${percent}%` }} />
          </div>
        </div>

        {/* Steps */}
        <ol className="mt-6 space-y-3">
          {stepList.map((step, idx) => {
            const Icon = step.done ? CheckCircle2 : Circle;
            return (
              <li key={step.key} className={`rounded-2xl border bg-card p-4 shadow-card sm:p-5 ${step.done ? "border-emerald-200 dark:border-emerald-900/40" : "border-border"}`}>
                <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-3">
                  <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${step.done ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" : "bg-accent text-primary"}`}>
                    <Icon className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <p className="flex items-baseline gap-2 font-semibold">
                      <span className="text-xs font-medium text-muted-foreground">Step {idx + 1}</span>
                      <span className="truncate">{step.title}</span>
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{step.body}</p>
                  </div>
                  {!step.done && step.action}
                </div>

                {/* Step 2 inline form: Personal info */}
                {"expand" in step && step.expand === "personal" && !step.done && (
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <Label htmlFor="fullName">Full legal name</Label>
                      <Input id="fullName" value={pi.fullName} onChange={(e) => setPi((p) => ({ ...p, fullName: e.target.value }))} placeholder="As it appears on your ID" />
                    </div>
                    <div>
                      <Label htmlFor="dob">Date of birth</Label>
                      <Input id="dob" type="date" value={pi.dateOfBirth} onChange={(e) => setPi((p) => ({ ...p, dateOfBirth: e.target.value }))} />
                    </div>
                    <div>
                      <Label htmlFor="phone">Mobile number</Label>
                      <Input id="phone" type="tel" value={pi.phone} onChange={(e) => setPi((p) => ({ ...p, phone: e.target.value }))} placeholder="07…" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="a1">Address line 1</Label>
                      <Input id="a1" value={pi.addressLine1} onChange={(e) => setPi((p) => ({ ...p, addressLine1: e.target.value }))} />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="a2">Address line 2 (optional)</Label>
                      <Input id="a2" value={pi.addressLine2} onChange={(e) => setPi((p) => ({ ...p, addressLine2: e.target.value }))} />
                    </div>
                    <div>
                      <Label htmlFor="city">City</Label>
                      <Input id="city" value={pi.city} onChange={(e) => setPi((p) => ({ ...p, city: e.target.value }))} />
                    </div>
                    <div>
                      <Label htmlFor="pc">Postcode</Label>
                      <Input id="pc" value={pi.postcode} onChange={(e) => setPi((p) => ({ ...p, postcode: e.target.value.toUpperCase() }))} placeholder="SW1A 1AA" />
                    </div>
                    <div className="sm:col-span-2 flex justify-end">
                      <Button onClick={onSavePersonal} disabled={busy}>
                        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save personal details
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 3 inline RTW declaration */}
                {"expand" in step && step.expand === "rtw" && !step.done && (
                  <div className="mt-4 rounded-xl bg-accent/40 p-4">
                    <label className="flex items-start gap-3 text-sm">
                      <input
                        type="checkbox"
                        checked={rtwChecked}
                        onChange={(e) => setRtwChecked(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-input"
                      />
                      <span>
                        <strong>I confirm that I have the legal right to work in the United Kingdom.</strong>
                        <span className="mt-1 block text-xs text-muted-foreground">
                          You will also need to upload supporting evidence (passport, BRP, or share-code) in Step 4 — this declaration alone does not verify your status.
                        </span>
                      </span>
                    </label>
                    <div className="mt-3 flex justify-end">
                      <Button onClick={onAckRtw} disabled={busy || !rtwChecked}>
                        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Confirm declaration
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 9 submit */}
                {"expand" in step && step.expand === "submit" && !step.done && (
                  <div className="mt-4 rounded-xl bg-accent/40 p-4">
                    <p className="text-sm">
                      {allBeforeReview
                        ? "All previous steps are complete. Submit your account for manual review."
                        : "Finish every step above, then come back to submit for review."}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      You will remain Pending Verification until a Peopels administrator approves your account. You will not appear in search, accept jobs, or receive payouts until then.
                    </p>
                    <div className="mt-3 flex justify-end">
                      <Button onClick={onSubmitForReview} disabled={busy || !canSubmit}>
                        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {state.status === "submitted" || state.status === "pending" ? "In review" : "Submit for review"}
                      </Button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ol>

        <div className="mt-6 rounded-2xl border border-border bg-card p-4 text-xs text-muted-foreground shadow-card sm:p-5">
          <p className="flex items-center gap-2 font-medium text-foreground"><Clock className="h-4 w-4" /> What happens next</p>
          <p className="mt-1">After submission, a human reviewer checks every document. We email and SMS you the outcome — usually within 24 hours on weekdays. If anything is unclear, we'll mark your account "More information required" with specific reviewer notes — you can re-upload and resubmit.</p>
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          <ChevronRight className="-mt-0.5 inline h-3 w-3" />{" "}
          <Link to="/dashboard" className="underline">Back to dashboard</Link>
        </p>
      </main>
      <Footer />
    </div>
  );
}
